import React from 'react';
import notify from '../../utils/notifyUtil';
import {
    buildArrivalWithShiftUpdates,
    hasCiDetayItems,
    parseCiDetayWithMeta,
    withCiDetayItems,
} from '../../utils/islemDetay';
import {
    confirmMissedBeforeReorder,
    confirmRandevuOteleme,
    formatMissedBeforeReorderMessage,
    formatRandevuShiftMessage,
} from '../../utils/islemRandevuShift';

/**
 * İşlem gridinde Gerçekleşti / CI_DETAY hücresi.
 * CI_DETAY varsa ana checkbox yok; her detay bağımsız işaretlenir.
 * İlk gelişte, önce gelmemiş randevu varsa gelen işlem onların üstüne alınır ve
 * açık randevular şablon aralığıyla yeniden dizilir. Önce gelmeyen yoksa
 * randevu gününden farklı geliş, sonraki tarihleri gün farkı kadar kaydırır.
 * Sonraki maddeler tarihi değiştirmez.
 */
export default function IslemGerceklestiCell({
    data,
    readOnly = false,
    islemList = [],
    casRef = null,
    sablonLines = [],
    onPersistUpdates,
}) {
    const { sablonAd, items: detayItems } = parseCiDetayWithMeta(data?.CI_DETAY);
    const mainLabel = sablonAd;
    const hasDetay = hasCiDetayItems(data?.CI_DETAY);
    const isComplete = !!data?.CI_ISL_TAR;
    const rowKey = Number(data?.CI_REF ?? data?.ci_ref ?? 0);

    const notifyShiftIfNeeded = (updates, deltaDays) => {
        const shifted = (updates || []).filter((u) => u.CI_RAN_TAR).length;
        if (shifted > 0 && deltaDays) {
            notify(formatRandevuShiftMessage(shifted, deltaDays), 'info', 2000);
        }
    };

    const acceptPlan = (plan) => {
        if (!plan?.needsConfirm) return true;
        if (plan.reorder) {
            return confirmMissedBeforeReorder(plan.reorder.earlierMissedCount, plan.reorder.laterOpenCount);
        }
        return confirmRandevuOteleme(plan.deltaDays);
    };

    const notifyPlan = (updates, plan) => {
        if (plan?.reorder) {
            notify(formatMissedBeforeReorderMessage(
                plan.reorder.earlierMissedCount,
                plan.reorder.laterOpenCount
            ), 'info', 2500);
            return;
        }
        notifyShiftIfNeeded(updates, plan?.deltaDays || 0);
    };

    const runPersist = async (updates, plan = null) => {
        if (typeof onPersistUpdates !== 'function' || !updates?.length) return false;
        const ok = await onPersistUpdates(data, updates);
        if (ok) notifyPlan(updates, plan);
        return ok;
    };

    const arrivalOptions = () => ({
        casRef: casRef ?? data?.CI_CAS_REF,
        sablonLines,
    });

    const markComplete = async () => {
        if (readOnly || isComplete) return;
        const plan = buildArrivalWithShiftUpdates(islemList, data, new Date(), arrivalOptions());
        if (!acceptPlan(plan)) return;
        await runPersist(plan.updates, plan);
    };

    const markIncomplete = async () => {
        if (readOnly || !isComplete) return;
        await runPersist([{ CI_REF: rowKey, CI_ISL_TAR: null }]);
    };

    const handleDetayToggle = async (itemIndex, checked) => {
        if (readOnly || !rowKey) return;
        const { items } = parseCiDetayWithMeta(data?.CI_DETAY);
        const now = new Date().toISOString();
        if (checked) {
            const nextItems = items.map((item, idx) => (
                idx === itemIndex ? { ...item, done: true } : item
            ));
            const nextDetay = withCiDetayItems(data?.CI_DETAY, nextItems);
            const firstArrival = !items.some((item) => item.done) && !data?.CI_ISL_TAR;
            if (firstArrival) {
                const plan = buildArrivalWithShiftUpdates(islemList, data, new Date(), arrivalOptions());
                if (!acceptPlan(plan)) return;
                const updates = plan.updates.map((u) => (
                    Number(u.CI_REF) === rowKey ? { ...u, CI_DETAY: nextDetay } : u
                ));
                await runPersist(updates, plan);
                return;
            }
            await runPersist([{
                CI_REF: rowKey,
                CI_DETAY: nextDetay,
                CI_ISL_TAR: data.CI_ISL_TAR || now,
            }]);
            return;
        }

        const nextItems = items.map((item, idx) => (
            idx === itemIndex ? { ...item, done: false } : item
        ));
        const anyDoneLeft = nextItems.some((item) => item.done);
        const nextDetay = withCiDetayItems(data?.CI_DETAY, nextItems);

        if (!anyDoneLeft) {
            await runPersist([{ CI_REF: rowKey, CI_ISL_TAR: null, CI_DETAY: nextDetay }]);
        } else {
            await runPersist([{ CI_REF: rowKey, CI_DETAY: nextDetay }]);
        }
    };

    const handleMainToggle = async (ev) => {
        if (readOnly) return;
        if (ev.target.checked) await markComplete();
        else await markIncomplete();
    };

    if (hasDetay) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '2px 0' }}>
                {mainLabel ? <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3 }}>{mainLabel}</span> : null}
                {detayItems.map((item, idx) => (
                    <label key={`${data?.CI_REF || 'row'}-detay-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
                        <input
                            type="checkbox"
                            checked={!!item.done}
                            disabled={readOnly}
                            onChange={(e) => handleDetayToggle(idx, e.target.checked)}
                            style={{ width: 16, height: 16, cursor: readOnly ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                        />
                        <span style={{ fontSize: 12, lineHeight: 1.2 }}>{item.ad}</span>
                    </label>
                ))}
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '2px 0' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
                <input
                    type="checkbox"
                    checked={isComplete}
                    disabled={readOnly}
                    onChange={handleMainToggle}
                    style={{ width: 18, height: 18, cursor: readOnly ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                />
                {mainLabel ? <span style={{ fontSize: 12, fontWeight: 600 }}>{mainLabel}</span> : null}
            </label>
        </div>
    );
}
